import React from 'react';
import styles from './Button.module.css';

/*
 * A NOTE ON THE TWO CHILDREN BELOW — they crashed the app on 2026-09-06 and the shape is easy
 * to "tidy" back into the bug.
 *
 * This used to render:
 *
 *     {loading ? <span className={styles.spinner} /> : null}
 *     {!loading && children}
 *
 * which is fine in English and fatal on a translated page. A translator replaces the label's
 * text node with a <font> wrapper; React still holds the original node. Flipping `loading` then
 * asks the DOM to do two impossible things — insertBefore(spinner, <the vanished text node>)
 * and removeChild(<the vanished text node>) — both of which throw NotFoundError, unmounting the
 * tree into the error boundary. Ray hit exactly this pressing Submit on a challenge.
 *
 * So: children are rendered UNCONDITIONALLY (the label is hidden while loading by
 * `.btn.loading { color: transparent }` — the conditional was always redundant with the CSS),
 * and the spinner comes LAST so React appends it rather than inserting it before the label.
 * Neither operation touches the translated text node.
 *
 * Side benefit: the button no longer collapses to padding-width mid-request.
 */

const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  children,
  className = '',
  type = 'button',
  ...props
}) => {
  const buttonClass = [
    styles.btn,
    styles[`btn-${variant}`],
    styles[`btn-${size}`],
    loading && styles.loading,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={buttonClass}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {/* ORDER AND UNCONDITIONALITY BOTH MATTER — see below. */}
      {children}
      {loading && <span className={styles.spinner} />}
    </button>
  );
};

export default Button;
